import { LabelChip, type LabelChipSize, type LabelChipVariant } from "@carbon6/c6-frontend-design";
import { HatTextTooltip, type HatTooltipPlacement } from "@/components/tooltip";
import { labelExplanation } from "./labelExplanation";

export type HatLabelChipProps = {
  // 설명 문구를 조회할 라벨 key/value.
  labelKey: string;
  value: string;
  color: number;
  // 서버 라벨 행의 설명. 값을 주면 툴팁이 이 문구를 쓴다(`labelExplanation` 규칙). 서버 행이
  // 없는 호출부(리뷰 초안 칩)는 값을 주지 않고 FE 가이드 폴백을 쓴다.
  description?: string | null;
  size?: LabelChipSize;
  mode?: LabelChipVariant;
  // 칩에 표시할 텍스트. 지정하지 않으면 `${labelKey}: ${value}`.
  children?: string;
  className?: string;
  // 가이드 툴팁 위치.
  placement?: HatTooltipPlacement;
  // 누르면 값을 켜고 끄는 토글 칩으로 만든다. 값을 주면 mode 기본값이 interactive 가 된다.
  onClick?: () => void;
  // 토글 칩의 on/off. `aria-pressed` 로 출력한다. `onClick` 과 함께 줄 때만 의미가 있다.
  pressed?: boolean;
  // 토글 칩 비활성화(네이티브 button 의 disabled). 정적 칩에는 의미가 없다.
  disabled?: boolean;
};

// 공용 라벨 칩. c6 LabelChip 에 값의 의미를 설명하는 툴팁을 자동으로 붙인다.
// `labelExplanation` 이 문구를 고른다. 서버 `description` 이 우선이고, 없으면 FE 가이드를 쓴다.
// 설명이 없는 라벨만 일반 LabelChip 과 같이 동작한다(라벨 텍스트를 native title 로 표시).
//
// `onClick` 을 주면 c6 의 `interactive` 칩으로 렌더링한다. 네이티브 `<button>` 이라 키보드·
// 포커스·disabled 를 추가 작업 없이 지원한다. 켜짐/꺼짐은 테두리가 아니라 **칩 색**으로
// 구분한다(꺼진 칩은 중립색 `color={0}`). outline 은 상태 표현으로 쓰지 않는다.
export function HatLabelChip({
  labelKey,
  value,
  color,
  description,
  size = "xs",
  mode,
  children,
  className,
  placement = "top",
  onClick,
  pressed,
  disabled,
}: HatLabelChipProps) {
  const text = children ?? `${labelKey}: ${value}`;
  const tooltipText = labelExplanation({ key: labelKey, value, description });

  const chip = (
    <LabelChip
      color={color}
      size={size}
      // c6 LabelChip 의 표시 축은 아직 `variant` 다. 앱 `mode` 를 c6 로 전달하는 줄은 이 줄뿐이다.
      variant={mode ?? (onClick === undefined ? "filled" : "interactive")}
      className={className}
      // 툴팁이 있으면 HatTextTooltip 이 hover 안내를 담당하므로 native title 은 비운다.
      // 툴팁이 없는 칩은 텍스트가 잘릴 경우에 대비해 텍스트를 native title 로 남긴다.
      title={tooltipText === null ? text : undefined}
      onClick={onClick}
      aria-pressed={pressed}
      disabled={disabled}
    >
      {text}
    </LabelChip>
  );

  if (tooltipText === null) {
    return chip;
  }
  return (
    <HatTextTooltip text={tooltipText} placement={placement}>
      {chip}
    </HatTextTooltip>
  );
}
